import { Link } from "react-router-dom";
import { type AdventureMap } from "~/lib/api";
import { routes } from "~/lib/routes";
import { ActionsMenu } from "~/components/ui/actions-menu";
import { Card } from "~/components/ui/card";

interface MapListItemProps {
  map: AdventureMap;
  deletingId: string | null;
  onDelete: (mapId: string, mapName: string) => void;
  onShare: (map: AdventureMap) => void;
}

export function MapListItem({ map, deletingId, onDelete, onShare }: MapListItemProps) {
  const { permissions } = map;

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <Card
      elevation="none"
      className="relative p-6 overflow-hidden ring-2 ring-transparent hover:ring-primary/20 has-[:focus-visible]:ring-primary/50 transition-shadow"
    >
      {/* Stretched link for accessibility */}
      <Link
        to={routes.to.maps.view(map.id)}
        className="absolute inset-0 z-0 outline-none"
        aria-label={`Edit ${map.name}`}
      />
      <div className="flex items-start gap-2 mb-2">
        <h3 className="flex-1 text-xl font-semibold text-foreground truncate">{map.name}</h3>
        <ActionsMenu
          className="relative z-10"
          actions={[
            [
              { icon: "edit", label: "Edit map", href: routes.to.maps.view(map.id) },
              permissions.canPublishLandingPage && {
                icon: "layout" as const,
                label: map.landingPage?.publishedAt ? "Edit landing page" : "Create landing page",
                href: routes.to.maps.landing(map.id),
              },
            ],
            [permissions.canShare && { icon: "share" as const, label: "Share", onClick: () => onShare(map) }],
            [
              permissions.canDelete && {
                icon: "trash" as const,
                label: deletingId === map.id ? "Deleting..." : "Delete",
                variant: "destructive" as const,
                disabled: deletingId === map.id,
                onClick: () => onDelete(map.id, map.name),
              },
            ],
          ]}
        />
      </div>
      {map.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">{map.description}</p>
      )}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>Created: {formatDate(map.createdAt)}</p>
        {map.updatedAt.getTime() !== map.createdAt.getTime() && <p>Updated: {formatDate(map.updatedAt)}</p>}
      </div>
    </Card>
  );
}
